import React, { useCallback } from 'react';
import { useAppDispatch } from '../../../hooks';
import { Transaction, TransactionType } from '../../../api/client'
import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography';
import AccountNumber from '../../common/AccountNumber';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import { colors } from '../../../colors';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import IncomingIcon from '@mui/icons-material/West';
import OutgoingIcon from '@mui/icons-material/East';
import FeeIcon from '@mui/icons-material/Toll';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NotFoundIcon from '@mui/icons-material/SearchOff';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { formatDate } from '../../Utils';
import currencyFormatter from 'currency-formatter';
import { actionCreators as DebtorLeapClaimsStoreActionCreators } from '../../../store/DebtorLeapClaimsStore';
import Box from '@mui/material/Box';

interface Props {
  kid?: string | undefined;
  accountNumber?: string | undefined;
  transactionsHaveBeenFetched?: boolean;
  transactions?: Transaction[];
}

const useStyles = makeStyles(() => ({
  content: {
    justifyContent: 'space-between'
  },
}));

export default function DebtorPaymentInformation(props: Props) {
  const classes = useStyles();

  const dispatch = useAppDispatch();

  const getLeapDebtCollectionEvents = useCallback((kid: string) => {
    dispatch(DebtorLeapClaimsStoreActionCreators.getDebtorClaimTransactionsOnKid(kid));
  }, [dispatch]);

  const [expanded, setExpanded] = React.useState(false);

  const handleExpansion = () => {
    let isExpanded = !expanded;
    setExpanded(isExpanded);
    if (isExpanded && !props.transactionsHaveBeenFetched && props.kid) {
      getLeapDebtCollectionEvents(props.kid);
    }
  };

  return (
    <Card
      sx={{
        maxWidth: '57%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 auto',
        backgroundColor: colors.primary.milkyway,
        marginTop: '16px',
        marginBottom: '10px',
      }}
      variant="outlined">
      <Accordion sx={{
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyItems: 'center',
        minWidth: '100%',
        margin: '0 auto',
      }}
        expanded={expanded}
        onChange={handleExpansion}
      >
        <AccordionSummary
          sx={{
            margin: '0 !important',
            backgroundColor: colors.primary.milkyway,
            marginTop: '16px',
            marginBottom: '10px'
          }}
          classes={{ content: classes.content }}
          expandIcon={<ArrowDropDownIcon />}
        >
          <Box onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              cursor: 'text',
              userSelect: 'text',
              display: 'flex',
              flexGrow: '1',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <Typography>KID-nummer: {props.kid}</Typography>
            <AccountNumber accountNumber={props.accountNumber} />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0', backgroundColor: colors.lightCyan }}>
          <TableContainer sx={{ backgroundColor: 'transparent', overflowX: 'hidden' }} component={Paper}>
            <Table size="small" aria-label="simple table">
              <TableHead sx={{
                '& th': {
                  verticalAlign: 'top',
                  padding: '2px 16px',
                  fontWeight: '600'
                }
              }}>
                <TableRow>
                  <TableCell sx={{ maxWidth: '0px' }}><MultipleStopIcon sx={{ paddingTop: '2px' }} fontSize="small" /></TableCell>
                  <TableCell align="left">Dato</TableCell>
                  <TableCell align="left">Beskrivelse</TableCell>
                  <TableCell align="left">Kontonummer</TableCell>
                  <TableCell align="right">Beløp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.transactionsHaveBeenFetched ?
                  props.transactions && props.transactions.length > 0 ?
                    props.transactions.map((row, i) =>
                      <TableRow
                      key={i}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '& td': { padding: '2px 16px' }
                      }}>
                        <TableCell sx={{ maxWidth: '0px' }}>
                          {row.transactionType === TransactionType.InboundPayment
                            ? <IncomingIcon sx={{ color: colors.secondary.bamboo, paddingTop: '4px' }} />
                            : row.transactionType === TransactionType.OutboundPayment ?
                              <OutgoingIcon sx={{ color: colors.secondary.candy, paddingTop: '4px' }} />
                              : <FeeIcon sx={{ color: colors.primary.bondiblue, paddingTop: '4px' }} />
                          }
                        </TableCell>
                        <TableCell align="left">{row.date ? formatDate(row.date) : "Behandles"}</TableCell>
                        <TableCell align="left">{row.description}</TableCell>
                        <TableCell align="left">{row.accountNumber}</TableCell>
                        <TableCell align="right">{row.amount
                          ? (currencyFormatter.format(row.amount, { code: 'NOK' }))
                          : "Gebyrfri"}</TableCell>
                      </TableRow>)

                    : <TableRow sx={{ '& td': { padding: '2px 16px' } }}>
                        <TableCell sx={{ maxWidth: '0px' }}><NotFoundIcon sx={{ color: colors.secondary.candy, paddingTop: '8px' }} fontSize="small" /></TableCell>
                        <TableCell sx={{ maxWidth: '0px' }} colSpan={4}>Ingen transaksjoner funnet på {props.kid}</TableCell>
                      </TableRow>

                    : <TableRow>
                        <TableCell sx={{ maxWidth: '0px' }}>
                          <Skeleton animation="wave" height="24px" />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton animation="wave" height="24px" />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton animation="wave" height="24px" />
                        </TableCell>
                        <TableCell align="left">
                          <Skeleton animation="wave" height="24px" />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton animation="wave" height="24px" />
                        </TableCell>
                      </TableRow>
                }
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
};